import React, { useEffect, useState } from 'react';
import styles from './CoachFeedbackCard.module.css';
import { CoachFeedback } from '../../../Utils/aiCoach';
import type { CoachStatus } from '../../../Redux/Store/Slices/questionnaire';

interface CoachFeedbackCardProps {
  feedback: CoachFeedback | undefined;
  isLoading: boolean;
  progressText?: string;
  /** True when the answer changed since this feedback was produced. */
  isStale: boolean;
  status?: CoachStatus;
  onRetry?: () => void;
}

const CoachFeedbackCard = ({
  feedback,
  isLoading,
  progressText,
  isStale,
  status,
  onRetry,
}: CoachFeedbackCardProps) => {
  const [collapsed, setCollapsed] = useState(true);

  // Collapse back to the summary view whenever fresh feedback arrives.
  useEffect(() => {
    setCollapsed(true);
  }, [feedback]);

  if (isLoading) {
    return (
      <div className={styles.card} role="status" aria-live="polite">
        <div className={styles.loadingRow}>
          <span className={styles.spinner} />
          <span className={styles.loadingText}>
            {progressText ?? 'Mia is reviewing your answer...'}
          </span>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className={`${styles.card} ${styles.errorCard}`} role="alert">
        <div className={styles.headerRow}>
          <span className={styles.miaLabel}>
            <img src="/img/sparkle.svg" alt="" />
            Mia Coach
          </span>
        </div>
        <p className={styles.summary}>Mia couldn't review this answer.</p>
        {onRetry && (
          <button type="button" className={styles.retryButton} onClick={onRetry}>
            Retry
          </button>
        )}
      </div>
    );
  }

  if (!feedback || feedback.quality === 'empty') {
    return null;
  }

  const hasDetail =
    feedback.missing.length > 0 ||
    feedback.evidenceConflicts.length > 0 ||
    feedback.highlights.length > 0;

  return (
    <div className={`${styles.card} ${isStale ? styles.stale : ''}`}>
      <div className={styles.headerRow}>
        <span className={styles.miaLabel}>
          <img src="/img/sparkle.svg" alt="" />
          Mia Coach
        </span>
        {isStale && <span className={styles.staleLabel}>Answer changed since this review</span>}
        {hasDetail && (
          <button
            type="button"
            className={styles.collapseButton}
            onClick={() => setCollapsed((prev) => !prev)}
            aria-expanded={!collapsed}
          >
            {collapsed ? 'Show details' : 'Hide details'}
          </button>
        )}
      </div>

      {feedback.summary && <p className={styles.summary}>{feedback.summary}</p>}

      {!collapsed && feedback.highlights.length > 0 && (
        <div className={styles.detailSection}>
          <span className={styles.detailTitle}>Suggestions in your answer</span>
          <ul className={styles.detailList}>
            {feedback.highlights.map((highlight, index) => (
              <li key={index}>
                <span className={styles.highlightQuote}>"{highlight.text}"</span>
                {' — '}
                {highlight.guidance}
              </li>
            ))}
          </ul>
        </div>
      )}

      {!collapsed && feedback.missing.length > 0 && (
        <div className={styles.detailSection}>
          <span className={styles.detailTitle}>You may want to consider</span>
          <ul className={styles.detailList}>
            {feedback.missing.map((point, index) => (
              <li key={index}>{point}</li>
            ))}
          </ul>
        </div>
      )}

      {!collapsed && feedback.evidenceConflicts.length > 0 && (
        <div className={styles.detailSection}>
          <span className={`${styles.detailTitle} ${styles.conflictTitle}`}>
            You may want to check against your evidence
          </span>
          <ul className={styles.detailList}>
            {feedback.evidenceConflicts.map((conflict, index) => (
              <li key={index}>
                <span className={styles.conflictQuote}>"{conflict.text}"</span>
                {' — '}
                {conflict.detail}
                {conflict.evidenceFile && (
                  <span className={styles.conflictFile}> ({conflict.evidenceFile})</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default CoachFeedbackCard;
